/**
 * EmbeddedAgentContext — Session-scoped state for all embedded agent runs
 *
 * Survives document switches within the same session. Each run tracks:
 * - Agent config, status, live transcript, streaming text, cost
 * - The selection context that triggered the run
 *
 * Placed at AppShell level so the mini-widget persists across file navigation.
 */
import { createContext, useContext, useState, useCallback, useRef, useMemo, type ReactNode } from 'react'
import type { Workspace } from '../engine/types'
import type {
    AgentConversationHistory,
    AgentRunStatus,
    TranscriptEvent,
    CostInfo,
} from '../engine/agentTypes'
import { parseAgentConfig } from '../engine/agentTypes'
import { createAgentRunController, resolveRefs } from '../engine/agentRuntime'
import { buildConversationHistoryFromActivity } from '../engine/agent/conversationHistory'
import { ensureAgentSessionDirectories } from '../engine/agent/sessionPaths'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface SelectionContext {
    filePath: string
    lineStart: number   // 1-based
    lineEnd: number     // 1-based inclusive
    selectedText: string
    /** When invoked at cursor (no selection), surrounding lines for orientation */
    surroundingText?: string
    /** Cursor line number when no text is selected (1-based) */
    cursorLine?: number
}

export interface EmbeddedAgentRun {
    id: string
    agentPath: string
    agentName: string
    status: AgentRunStatus
    latestAction: string
    transcript: TranscriptEvent[]
    streamingText: string
    currentCost: CostInfo
    selectionContext?: SelectionContext
    expanded: boolean
    transcriptPath: string
}

export interface EmbeddedAgentsAPI {
    runs: EmbeddedAgentRun[]
    startRun: (agentPath: string, message: string, context?: SelectionContext) => Promise<void>
    sendMessage: (runId: string, text: string) => void
    stopRun: (runId: string) => void
    dismissRun: (runId: string) => void
    toggleExpanded: (runId: string) => void
}

// ────────────────────────────────────────────────────────────────
// Context
// ────────────────────────────────────────────────────────────────

const EmbeddedAgentCtx = createContext<EmbeddedAgentsAPI | null>(null)

export function useEmbeddedAgents(): EmbeddedAgentsAPI {
    const ctx = useContext(EmbeddedAgentCtx)
    if (!ctx) throw new Error('useEmbeddedAgents must be inside EmbeddedAgentProvider')
    return ctx
}

// ────────────────────────────────────────────────────────────────
// Human-readable action summaries
// ────────────────────────────────────────────────────────────────

function summarizeEvent(event: TranscriptEvent): string {
    switch (event.type) {
        case 'assistant_thinking': return '🧠 Tänker…'
        case 'assistant_stream_delta': return '✍️ Skriver…'
        case 'assistant_message_final': return '✅ Klart'
        case 'tool_call': {
            const name = event.name ?? ''
            if (name.includes('read')) return `📖 Läser ${(event.args as Record<string, string>)?.path?.split('/').pop() ?? ''}…`
            if (name.includes('write')) return `✏️ Skriver ${(event.args as Record<string, string>)?.path?.split('/').pop() ?? ''}…`
            if (name.includes('list')) return '📂 Listar filer…'
            if (name.includes('search')) return `🔍 Söker "${String((event.args as Record<string, string>)?.query ?? '').slice(0, 30)}"…`
            if (name.includes('stat')) return `🧾 Inspekterar ${(event.args as Record<string, string>)?.path?.split('/').pop() ?? ''}…`
            if (name.includes('commit')) return '🕘 Läser historik…'
            if (name.includes('patch')) return `🩹 Redigerar ${(event.args as Record<string, string>)?.path?.split('/').pop() ?? ''}…`
            if (name.includes('rename')) return `↪️ Byter namn på ${(event.args as Record<string, string>)?.fromPath?.split('/').pop() ?? ''}…`
            if (name.includes('delete')) return `🗑️ Raderar ${(event.args as Record<string, string>)?.path?.split('/').pop() ?? ''}…`
            return `🔧 ${name}…`
        }
        case 'tool_result': return '✅ Verktyg klart'
        case 'error': return '❌ Fel'
        default: return ''
    }
}

// ────────────────────────────────────────────────────────────────
// Format selection context into user message
//
// DESIGN: The message is structured so the LLM can distinguish:
//   [TARGET]      → the exact text the user is referring to with "this/detta"
//   [CONTEXT]     → read-only surrounding lines for orientation (DO NOT edit)
//   [INSTRUCTION] → clear action directives
// ────────────────────────────────────────────────────────────────

export function formatContextMessage(message: string, ctx?: SelectionContext): string {
    if (!ctx) return message

    const isCursorOnly = !ctx.selectedText && ctx.cursorLine != null

    if (isCursorOnly) {
        // ── Cursor-only mode: user pressed Ctrl+I without selecting text ──
        return [
            `[FILE] ${ctx.filePath}`,
            `[CURSOR] Line ${ctx.cursorLine}`,
            '',
            ctx.surroundingText
                ? `[SURROUNDING_CONTEXT — read-only, for orientation only, do NOT treat as the target]\n${ctx.surroundingText}`
                : '',
            '',
            `[USER_REQUEST]\n${message}`,
            '',
            `[INSTRUCTIONS]`,
            `The user's cursor is at line ${ctx.cursorLine} in ${ctx.filePath}.`,
            `Use secureblob_read_text to read the file first, then use secureblob_patch_text to make edits near line ${ctx.cursorLine}.`,
            `ALWAYS apply changes with tools — NEVER just reply with the changed text.`,
        ].filter(Boolean).join('\n')
    }

    // ── Selection mode: user selected text ──
    const lineInfo = ctx.lineStart === ctx.lineEnd
        ? `Line ${ctx.lineStart}`
        : `Lines ${ctx.lineStart}–${ctx.lineEnd}`
    return [
        `[FILE] ${ctx.filePath}`,
        `[SELECTION] ${lineInfo}`,
        '',
        `[TARGET — this is what the user is referring to with "this"/"detta"/"det här"]`,
        ctx.selectedText,
        '',
        ctx.surroundingText
            ? `[SURROUNDING_CONTEXT — read-only, for orientation only, do NOT edit this]\n${ctx.surroundingText}`
            : '',
        '',
        `[USER_REQUEST]\n${message}`,
        '',
        `[INSTRUCTIONS]`,
        `The user selected the text in [TARGET] from ${ctx.filePath} (${lineInfo}).`,
        `When asked to change/translate/fix/improve, use secureblob_patch_text with the selected text as the "search" parameter and your improved version as "replace".`,
        `ALWAYS apply changes with tools — NEVER just reply with the changed text.`,
    ].filter(Boolean).join('\n')
}

// ────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────

interface ProviderProps {
    workspace: Workspace
    children: ReactNode
}

export function EmbeddedAgentProvider({ workspace, children }: ProviderProps) {
    const [runs, setRuns] = useState<EmbeddedAgentRun[]>([])
    const controllersRef = useRef<Map<string, ReturnType<typeof createAgentRunController>>>(new Map())

    // ── Helper: update a single run's field ──────────────────
    const updateRun = useCallback((runId: string, patch: Partial<EmbeddedAgentRun>) => {
        setRuns(prev => prev.map(r => r.id === runId ? { ...r, ...patch } : r))
    }, [])

    // ── Start a new embedded run ─────────────────────────────
    const startRun = useCallback(async (agentPath: string, message: string, context?: SelectionContext) => {
        // Load agent config
        const configText = await workspace.fs.readText(agentPath)
        if (!configText) return
        const config = parseAgentConfig(configText)
        if (!config) return

        const runId = `emb-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
        const fullMessage = formatContextMessage(message, context)

        const runPaths = ensureAgentSessionDirectories(workspace, agentPath, `session-${runId}`)
        const transcriptPath = runPaths.transcriptPath

        // Create embed run state
        const newRun: EmbeddedAgentRun = {
            id: runId,
            agentPath,
            agentName: config.name,
            status: 'running',
            latestAction: '🚀 Startar…',
            transcript: [],
            streamingText: '',
            currentCost: { tokensIn: 0, tokensOut: 0, estimatedCostUsd: 0 },
            selectionContext: context,
            expanded: false,
            transcriptPath,
        }
        setRuns(prev => [newRun, ...prev])

        // Create controller
        const controller = createAgentRunController(config, workspace)
        controllersRef.current.set(runId, controller)

        // Resolve refs & build history
        const refs = await resolveRefs(fullMessage, workspace, config)
        const history: AgentConversationHistory = await buildConversationHistoryFromActivity(config, [], workspace)
        const activity: TranscriptEvent[] = []

        // Add user message to transcript
        const userEvent: TranscriptEvent = {
            type: 'user_message',
            text: fullMessage,
            refs,
            timestamp: Date.now(),
        }
        activity.push(userEvent)
        setRuns(prev => prev.map(r => r.id === runId
            ? { ...r, transcript: [...r.transcript, userEvent] }
            : r
        ))

        // Run the agent
        let accumulated = ''
        try {
            const iter = controller.run(fullMessage, refs, undefined, history)
            for await (const event of iter) {
                if (event.type === 'assistant_message_final') {
                    activity.push({ ...event, text: accumulated || event.text })
                } else {
                    activity.push(event)
                }
                const action = summarizeEvent(event)

                if (event.type === 'tool_call') {
                    setRuns(prev => prev.map(r => {
                        if (r.id !== runId) return r
                        const idx = r.transcript.findIndex(e => e.type === 'tool_call' && e.id === event.id)
                        const transcript = idx >= 0
                            ? [...r.transcript.slice(0, idx), event, ...r.transcript.slice(idx + 1)]
                            : [...r.transcript, event]
                        return { ...r, transcript, latestAction: action || r.latestAction }
                    }))
                } else if (event.type === 'assistant_stream_delta') {
                    accumulated += event.text
                    setRuns(prev => prev.map(r =>
                        r.id === runId ? { ...r, streamingText: accumulated, latestAction: action || r.latestAction } : r
                    ))
                } else if (event.type === 'assistant_message_final') {
                    setRuns(prev => prev.map(r => {
                        if (r.id !== runId) return r
                        return {
                            ...r,
                            transcript: [...r.transcript, { ...event, text: accumulated || event.text }],
                            streamingText: '',
                            latestAction: '✅ Klart',
                        }
                    }))
                } else if (event.type === 'assistant_tool_batch' || event.type === 'assistant_reasoning_item') {
                    continue
                } else if (event.type === 'cost_update') {
                    setRuns(prev => prev.map(r =>
                        r.id === runId
                            ? { ...r, currentCost: { tokensIn: event.tokensIn, tokensOut: event.tokensOut, estimatedCostUsd: event.estimatedCostUsd } }
                            : r
                    ))
                } else {
                    setRuns(prev => prev.map(r =>
                        r.id === runId
                            ? { ...r, transcript: [...r.transcript, event], latestAction: action || r.latestAction }
                            : r
                    ))
                }
            }

            updateRun(runId, { status: 'completed', latestAction: '✅ Klart', streamingText: '' })
            // Auto-dismiss completed runs after 10s
            setTimeout(() => dismissRun(runId), 10_000)
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                if (accumulated) {
                    const cancelledEvent: TranscriptEvent = {
                        type: 'assistant_message_final' as const,
                        text: accumulated + '\n\n*(avbruten)*',
                        timestamp: Date.now(),
                    }
                    activity.push(cancelledEvent)
                    setRuns(prev => prev.map(r =>
                        r.id === runId
                            ? {
                                ...r,
                                transcript: [...r.transcript, cancelledEvent],
                                status: 'cancelled' as const,
                                latestAction: '⏹ Avbruten',
                                streamingText: '',
                            }
                            : r
                    ))
                } else {
                    updateRun(runId, { status: 'cancelled', latestAction: '⏹ Avbruten', streamingText: '' })
                }
                setTimeout(() => dismissRun(runId), 8_000)
            } else {
                const errorEvent: TranscriptEvent = {
                    type: 'error' as const,
                    message: String(err),
                    retryable: true,
                    timestamp: Date.now(),
                }
                activity.push(errorEvent)
                setRuns(prev => prev.map(r =>
                    r.id === runId
                        ? {
                            ...r,
                            transcript: [...r.transcript, errorEvent],
                            status: 'error' as const,
                            latestAction: '❌ Fel',
                            streamingText: '',
                        }
                        : r
                ))
                setTimeout(() => dismissRun(runId), 15_000)
            }
        } finally {
            controllersRef.current.delete(runId)
        }

        // Persist transcript using latest state (avoid stale closure)
        setRuns(prev => {
            const finalRun = prev.find(r => r.id === runId)
            if (finalRun && finalRun.transcript.length > 0) {
                void (async () => {
                    const conversation = await buildConversationHistoryFromActivity(config, activity, workspace)
                    const transcriptData = {
                        schema: 'chat-transcript/v2',
                        agentPath: runPaths.baseAgentPath,
                        sessionId: runId,
                        config,
                        uiEvents: finalRun.transcript,
                        activity,
                        conversation,
                        cost: finalRun.currentCost,
                        updatedAt: Date.now(),
                    }
                    ensureAgentSessionDirectories(workspace, agentPath, `session-${runId}`)
                    workspace.fs.writeText(finalRun.transcriptPath, JSON.stringify(transcriptData, null, 2))
                })()
            }
            return prev
        })
    }, [workspace, updateRun])

    // ── Send follow-up message ───────────────────────────────
    const sendMessage = useCallback((runId: string, text: string) => {
        // For MVP, follow-up messages require opening the full chat
        // The mini-widget "Open full chat" action handles this
        void runId
        void text
    }, [])

    // ── Stop a running agent ──────────────────────────────────
    const stopRun = useCallback((runId: string) => {
        const controller = controllersRef.current.get(runId)
        if (controller) {
            updateRun(runId, { status: 'stopping' })
            controller.abort()
        }
    }, [updateRun])

    // ── Dismiss a run from the mini-widget ────────────────────
    const dismissRun = useCallback((runId: string) => {
        // Stop if still running
        const controller = controllersRef.current.get(runId)
        if (controller) controller.abort()
        controllersRef.current.delete(runId)
        setRuns(prev => prev.filter(r => r.id !== runId))
    }, [])

    // ── Toggle expanded state ─────────────────────────────────
    const toggleExpanded = useCallback((runId: string) => {
        setRuns(prev => prev.map(r =>
            r.id === runId ? { ...r, expanded: !r.expanded } : r
        ))
    }, [])

    const api = useMemo<EmbeddedAgentsAPI>(() => ({
        runs,
        startRun,
        sendMessage,
        stopRun,
        dismissRun,
        toggleExpanded,
    }), [runs, startRun, sendMessage, stopRun, dismissRun, toggleExpanded])

    return (
        <EmbeddedAgentCtx.Provider value={api}>
            {children}
        </EmbeddedAgentCtx.Provider>
    )
}
